import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Loader } from "@/components/Bits";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import VerifyEmail from "@/pages/VerifyEmail";
import GuardianVerify from "@/pages/GuardianVerify";
import AuthCallback from "@/pages/AuthCallback";
import Onboarding from "@/pages/Onboarding";
import Curriculum from "@/pages/Curriculum";
import Dashboard from "@/pages/Dashboard";
import Library from "@/pages/Library";
import TopicPage from "@/pages/TopicPage";
import ChapterPage from "@/pages/ChapterPage";
import TopicTest from "@/pages/TopicTest";
import Practice from "@/pages/Practice";
import Stuck from "@/pages/Stuck";
import Profile from "@/pages/Profile";
import About from "@/pages/About";
import Terms from "@/pages/Terms";
import Privacy from "@/pages/Privacy";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import ProgressPage from "@/pages/Progress";
import GuardianDashboard from "@/pages/GuardianDashboard";
import AgentPrompts from "@/pages/AgentPrompts";
import AdminPortal from "@/pages/AdminPortal";
import NotFound from "@/pages/NotFound";
import { ErrorBoundary } from "@/components/ErrorBoundary";

const Protected = ({ children, role }) => {
  const { user, loading } = useAuth();
  if (loading) return <Loader label="Checking your session…" />;
  if (!user) return <Navigate to="/login" replace />;
  if (role && user.role !== role) return <Navigate to={user.role === "guardian" ? "/guardian" : user.role === "admin" ? "/admin" : "/dashboard"} replace />;
  if (user.role === "student" && !user.profile_complete && window.location.pathname !== "/onboarding")
    return <Navigate to="/onboarding" replace />;
  return children;
};

const AppRoutes = () => {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/guardian/verify" element={<GuardianVerify />} />
      <Route path="/onboarding" element={<Protected><Onboarding /></Protected>} />
      <Route path="/curriculum" element={<Protected role="student"><Curriculum /></Protected>} />
      <Route path="/dashboard" element={<Protected role="student"><Dashboard /></Protected>} />
      <Route path="/library" element={<Protected role="student"><Library /></Protected>} />
      <Route path="/chapter/:chapterId" element={<Protected role="student"><ChapterPage /></Protected>} />
      <Route path="/topic/:topicId" element={<Protected role="student"><TopicPage /></Protected>} />
      <Route path="/topic/:topicId/test" element={<Protected role="student"><TopicTest /></Protected>} />
      <Route path="/practice" element={<Protected role="student"><Practice /></Protected>} />
      <Route path="/stuck" element={<Protected role="student"><Stuck /></Protected>} />
      <Route path="/profile" element={<Protected role="student"><Profile /></Protected>} />
      <Route path="/about" element={<About />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/progress" element={<Protected role="student"><ProgressPage /></Protected>} />
      <Route path="/agents" element={<Protected><AgentPrompts /></Protected>} />
      <Route path="/guardian" element={<Protected role="guardian"><GuardianDashboard /></Protected>} />
      <Route path="/admin" element={<Protected role="admin"><AdminPortal /></Protected>} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

export default function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}
